import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Download,
  FileText,
  Lock,
  Package,
  Plus,
  Save,
  ShieldCheck
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { downloadText, exportR2Case, exportReview, listReviews, loadR2Case, saveReview, uploadSyntheticCase } from "../api/client";
import {
  createBF001OfficeReview,
  flowEvidenceFor,
  type BuyerFundingFlowReview
} from "../domain/buyerFundingFlowSmoke";
import { BuyerFundingFlowApp } from "./BuyerFundingFlowApp";
import { EscrowFileDashboard } from "./EscrowFileDashboard";
import { CORE_MODEL, FLOW_NAME, PRODUCT_LINE, RECEIPT_NAME, STANDARD, TRUST_BOUNDARY } from "../domain/constants";
import { FIXTURES, createReviewFixture } from "../domain/fixtures";
import { formatMoney } from "../domain/money";
import { computeMatches, fieldCrossReference, inferOpenGaps, sourceRowsFor, statusLine } from "../domain/matchEngine";
import type { BuyerFundingReview, ReceiptPackage } from "../domain/types";
import { getPath, setPath } from "./path";
import { REVIEW_STEPS, type FieldDef } from "./reviewSteps";
import { BuyerPortalRoute } from "./BuyerPortalRoute";
import { VetoLogo } from "./VetoLogo";
import { buyerPortalPath, buyerPortalUrl } from "../domain/buyerPortal";
import { buildFeasibleSpecPackage } from "../domain/feasiblePackage";
import { createSaveBackZipBlob, downloadBlob } from "../domain/saveBackZip";

function routeKind(): "flow" | "office-file" | "office-review" | "review-evidence" | "home" {
  if (typeof window === "undefined") return "home";
  if (window.location.hash.startsWith("#/buyer/")) return "review-evidence";
  const path = window.location.pathname;
  if (path === "/office/review" || path.startsWith("/office/review/")) return "office-review";
  if (path === "/office" || path.startsWith("/office/")) return "office-file";
  const fileMatch = path.match(/^\/files\/([^/]+)\/?$/);
  if (fileMatch) return "flow";
  return "home";
}

function fileCodeFromPath(): string {
  const match = window.location.pathname.match(/^\/files\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : "";
}

function officeFileCodeFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  return (params.get("file") || "BF-001").trim().toUpperCase();
}

function OfficeFileRoute() {
  const [fileCode, setFileCode] = useState(officeFileCodeFromUrl);

  useEffect(() => {
    const refresh = () => setFileCode(officeFileCodeFromUrl());
    window.addEventListener("popstate", refresh);
    return () => window.removeEventListener("popstate", refresh);
  }, []);

  return (
    <EscrowFileDashboard
      fileCode={fileCode}
      onSelectFile={(code) => {
        const next = `/office?file=${encodeURIComponent(code)}`;
        window.history.pushState({}, "", next);
        setFileCode(code.toUpperCase());
      }}
    />
  );
}

export function App() {
  const [route, setRoute] = useState(routeKind);

  useEffect(() => {
    const refresh = () => setRoute(routeKind());
    window.addEventListener("hashchange", refresh);
    window.addEventListener("popstate", refresh);
    return () => {
      window.removeEventListener("hashchange", refresh);
      window.removeEventListener("popstate", refresh);
    };
  }, []);

  if (route === "review-evidence") {
    return <BuyerPortalRoute />;
  }
  if (route === "flow") {
    return <BuyerFundingFlowApp fileCode={fileCodeFromPath()} />;
  }
  if (route === "office-file") {
    return <OfficeFileRoute />;
  }
  if (route === "office-review") {
    return <OfficeApp />;
  }
  return <HomeRoute />;
}

function HomeRoute() {
  return (
    <div className="min-h-screen bg-[#f5f2ea] p-8 text-[#191919]">
      <main className="mx-auto max-w-xl space-y-4">
        <section className="panel">
          <VetoLogo height={36} className="mb-3" />
          <p className="label">Veto settlement flows</p>
          <h1 className="mt-2 text-3xl font-bold">Buyer Funding Flow</h1>
          <p className="mt-2 text-sm text-[#596158]">
            Secure funding instructions for the buyer — lookup, instructions, optional Plaid, acknowledgment.
          </p>
          <a className="button-primary mt-4 inline-flex" href="/files/BF-001">
            Open sample file BF-001
          </a>
        </section>
        <section className="panel">
          <p className="label">Office product (separate)</p>
          <h2 className="mt-1 text-lg font-bold">Buyer Funding Review</h2>
          <p className="mt-2 text-sm text-[#596158]">
            16-step office receipt before relying on inbound buyer funds. Not the buyer funding flow.
          </p>
          <a className="button-primary mt-3 inline-flex" href="/office">
            Open escrow file (BF-001)
          </a>
          <a className="button-secondary mt-2 inline-flex" href="/office/review">
            Open Buyer Funding Review
          </a>
        </section>
        <p className="text-xs text-[#596158]">
          See <a href="https://github.com/TryVeto/Buyer-Funding/blob/main/docs/PRODUCT-SPLIT.md">docs/PRODUCT-SPLIT.md</a> for
          Buyer Funding vs Seller Proceeds vs Payoff.
        </p>
      </main>
    </div>
  );
}

function OfficeApp() {
  const [reviews, setReviews] = useState<BuyerFundingReview[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [activeStep, setActiveStep] = useState(0);
  const [receipt, setReceipt] = useState<ReceiptPackage | null>(null);
  const [notice, setNotice] = useState(
    "Office review uses D1 + R2 when you run npm run dev (Wrangler). Vite-only dev falls back to localStorage."
  );

  useEffect(() => {
    listReviews().then((loaded) => {
      setReviews(loaded);
      setActiveId(loaded[0]?.id || "");
    });
  }, []);

  const activeReview = useMemo(
    () => reviews.find((review) => review.id === activeId) || reviews[0],
    [activeId, reviews]
  );

  const computed = useMemo(() => {
    if (!activeReview) return null;
    const withSources = { ...activeReview, sourceRows: sourceRowsFor(activeReview) };
    const matches = computeMatches(withSources);
    const gaps = inferOpenGaps({ ...withSources, matches });
    return { ...withSources, matches, openGaps: gaps };
  }, [activeReview]);

  const step = REVIEW_STEPS[activeStep];

  async function persist(review: BuyerFundingReview) {
    const saved = await saveReview(review);
    setReviews((current) => [saved, ...current.filter((candidate) => candidate.id !== saved.id)]);
    setActiveId(saved.id);
    setNotice("Review saved. Event and source rows are ready for receipt export.");
  }

  function updateField(field: FieldDef, raw: string | boolean) {
    if (!activeReview) return;
    const value = field.type === "number" ? Number(raw) : raw;
    const next = setPath(activeReview, field.path, value);
    preserveMoneyCurrency(next, field.path);
    next.updatedAt = new Date().toISOString();
    setReviews((current) => current.map((review) => (review.id === next.id ? next : review)));
  }

  async function handleExport() {
    if (!computed) return;
    const pkg = await exportReview(computed);
    setReceipt(pkg);
    downloadText(`${computed.reviewNumber}-receipt.html`, pkg.receiptHtml, "text/html");
    downloadText(`${computed.reviewNumber}-packet.json`, pkg.packetJson, "application/json");
    setNotice("Receipt package exported. HTML receipt and packet JSON were generated.");
  }

  async function handleExportZip() {
    if (!computed) return;
    const pkg = await exportReview(computed);
    setReceipt(pkg);
    const blob = await createSaveBackZipBlob(computed);
    downloadBlob(blob, `${computed.reviewNumber}-saveback-v${computed.receiptVersion}.zip`);
    setNotice("Save-back ZIP exported with receipt, manifests, policy gates, and buyer portal packet.");
  }

  async function copyBuyerPortalLink() {
    if (!computed) return;
    await navigator.clipboard.writeText(buyerPortalUrl(computed));
    setNotice("Buyer evidence portal link copied. Scoped local link; office remains decision-maker.");
  }

  function openBuyerPortal() {
    if (!computed) return;
    window.location.hash = buyerPortalPath(computed);
  }

  async function handleR2SyntheticPipeline() {
    try {
      const manifest = await uploadSyntheticCase("source-mismatch");
      const loaded = await loadR2Case(manifest.caseId);
      const exported = await exportR2Case(manifest.caseId);
      setReviews((current) => [loaded, ...current.filter((candidate) => candidate.id !== loaded.id)]);
      setActiveId(loaded.id);
      setNotice(`Synthetic case stored in R2, read back, and exported. Receipt key: ${exported.receiptKey}`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "R2 synthetic pipeline failed.");
    }
  }

  function createNewReview() {
    const id = `manual-${Date.now()}`;
    const review = createReviewFixture(id, "Manual review", {
      id,
      reviewNumber: `BFR-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
      fileNumber: "",
      buyerParties: [{ id: `${id}-buyer`, name: "", role: "buyer", expectedFundingParty: true }],
      status: "Draft",
      fundsStatus: "Not requested",
      expectedAmount: undefined,
      expectedFunds: {
        amount: undefined,
        purpose: "Cash to close",
        sourceStatus: "unknown"
      },
      inboundReceipt: { sourceType: "not_received" },
      matches: [],
      openGaps: [],
      events: []
    });
    setReviews((current) => [review, ...current]);
    setActiveId(review.id);
    setActiveStep(0);
    setNotice("New Buyer Funding Review created.");
  }

  function loadBF001FlowSmoke() {
    const review = createBF001OfficeReview();
    setReviews((current) => [review, ...current.filter((candidate) => candidate.id !== review.id)]);
    setActiveId(review.id);
    setActiveStep(0);
    setNotice("BF-001 fake flow smoke file created with instruction version 1 published.");
  }

  if (!activeReview || !computed) {
    return <div className="p-8">Loading Buyer Funding Review...</div>;
  }

  return (
    <div className="min-h-screen bg-[#f5f2ea] text-[#191919]">
      <header className="border-b border-[#d8d2c4] bg-[#fffdf8] px-6 py-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#596158]">{PRODUCT_LINE}</p>
            <h1 className="mt-1 text-3xl font-bold tracking-normal">Buyer Funding Review</h1>
            <p className="mt-1 text-sm text-[#596158]">Office product · {FLOW_NAME}</p>
            <p className="mt-1 max-w-5xl text-sm text-[#596158]">{STANDARD}</p>
            <a className="mt-2 inline-block text-sm font-bold underline" href="/office">
              ← Back to escrow file
            </a>
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="button-secondary" onClick={() => setReviews(FIXTURES)}>
              <Archive size={16} /> Load fixtures
            </button>
            <button className="button-secondary" onClick={handleR2SyntheticPipeline}>
              <Package size={16} /> R2 smoke case
            </button>
            <button className="button-secondary" data-testid="load-bf001" onClick={loadBF001FlowSmoke}>
              <FileText size={16} /> BF-001 smoke file
            </button>
            <button className="button-primary" onClick={createNewReview}>
              <Plus size={16} /> New review
            </button>
          </div>
        </div>
      </header>

      <main className="grid gap-4 p-4 xl:grid-cols-[300px_minmax(0,1fr)_380px]">
        <aside className="space-y-4">
          <div className="panel">
            <p className="label">Trust boundary</p>
            <p className="mt-2 text-sm text-[#4f554e]">{TRUST_BOUNDARY}</p>
          </div>
          <div className="panel">
            <div className="flex items-center justify-between">
              <p className="label">Reviews</p>
              <span className="text-xs text-[#596158]">{reviews.length}</span>
            </div>
            <div className="mt-3 space-y-2">
              {reviews.map((review) => (
                <button
                  key={review.id}
                  className={`w-full rounded-md border p-3 text-left ${
                    review.id === activeId ? "border-[#1f6b57] bg-[#e4efe9]" : "border-[#d8d2c4] bg-white"
                  }`}
                  onClick={() => setActiveId(review.id)}
                >
                  <strong className="block text-sm">{review.reviewNumber}</strong>
                  <span className="block text-xs text-[#596158]">{review.fileNumber || "No file number"}</span>
                  <span className="mt-1 block text-xs">{review.officeAction.action}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        <section className="panel min-w-0">
          <div className="flex flex-col gap-4 border-b border-[#d8d2c4] pb-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="label">Step {activeStep + 1} of {REVIEW_STEPS.length}</p>
              <h2 className="mt-1 text-2xl font-bold">{step.title}</h2>
              <p className="mt-1 max-w-3xl text-sm text-[#596158]">{step.help}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="button-secondary" onClick={() => persist(computed)}>
                <Save size={16} /> Save
              </button>
              <button className="button-primary" onClick={handleExport}>
                <Download size={16} /> Export
              </button>
            </div>
          </div>

          <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
            {REVIEW_STEPS.map((candidate, index) => (
              <button
                key={candidate.id}
                className={`rounded-md border px-3 py-2 text-left text-sm ${
                  index === activeStep ? "border-[#1f6b57] bg-[#e4efe9]" : "border-[#d8d2c4] bg-[#fffdf8]"
                }`}
                onClick={() => setActiveStep(index)}
              >
                <span className="font-bold">{index + 1}.</span> {candidate.title}
              </button>
            ))}
          </div>

          {step.id === "match" ? (
            <MatchTable review={computed} />
          ) : step.id === "check" ? (
            <CheckAnswers review={computed} />
          ) : step.id === "export" ? (
            <ExportPanel
              review={computed}
              receipt={receipt}
              onExport={handleExport}
              onExportZip={handleExportZip}
              onCopyBuyerLink={copyBuyerPortalLink}
              onOpenBuyerPortal={openBuyerPortal}
            />
          ) : (
            <form className="mt-5 grid gap-4 md:grid-cols-2" onSubmit={(event) => event.preventDefault()}>
              {step.fields.map((field) => (
                <FormField
                  key={field.path}
                  field={field}
                  value={getPath(activeReview, field.path)}
                  onChange={(value) => updateField(field, value)}
                />
              ))}
            </form>
          )}

          <div className="mt-6 flex justify-between border-t border-[#d8d2c4] pt-4">
            <button className="button-secondary" disabled={activeStep === 0} onClick={() => setActiveStep((value) => Math.max(0, value - 1))}>
              Back
            </button>
            <button className="button-primary" onClick={() => setActiveStep((value) => Math.min(REVIEW_STEPS.length - 1, value + 1))}>
              Next step
            </button>
          </div>
        </section>

        <aside className="space-y-4">
          <div className="panel">
            <p className="label">Receipt object</p>
            <h2 className="mt-2 text-xl font-bold">{RECEIPT_NAME}</h2>
            <p className="mt-2 text-sm text-[#596158]">{CORE_MODEL}</p>
            <p className="mt-3 rounded-md border border-[#1f6b57] bg-[#e4efe9] p-3 text-sm font-semibold">
              {statusLine(computed)}
            </p>
          </div>
          <div className="panel">
            <p className="label">System notice</p>
            <p className="mt-2 text-sm text-[#596158]">{notice}</p>
          </div>
          <SummaryCard title="Four-way match" icon={<ShieldCheck size={17} />}>
            <ul className="space-y-2">
              {computed.matches.map((match) => (
                <li className="flex gap-2 text-sm" key={match.id}>
                  {match.result === "Matched" ? <CheckCircle2 className="text-[#1f6b57]" size={16} /> : <AlertTriangle className="text-[#8a5a10]" size={16} />}
                  <span><strong>{match.result}</strong> {match.label}</span>
                </li>
              ))}
            </ul>
          </SummaryCard>
          <SummaryCard title="Open gaps" icon={<AlertTriangle size={17} />}>
            <ul className="space-y-2">
              {computed.openGaps.length ? computed.openGaps.map((gap) => (
                <li className="text-sm" key={gap.id}><strong>{gap.gapType}</strong>: {gap.status}</li>
              )) : <li className="text-sm">No open gaps recorded.</li>}
            </ul>
          </SummaryCard>
          <SummaryCard title="Source package" icon={<Package size={17} />}>
            <p className="text-sm">{computed.sourceRows.length} source rows · {computed.events.length} events · {fieldCrossReference(computed).length} cross-reference rows</p>
          </SummaryCard>
        </aside>
      </main>
    </div>
  );
}

function FormField({ field, value, onChange }: { field: FieldDef; value: unknown; onChange: (value: string | boolean) => void }) {
  const id = field.path.replaceAll(".", "-");
  const fieldValue = value ?? "";
  const inputValue = field.type === "datetime-local" ? toDatetimeLocalValue(fieldValue) : String(fieldValue);
  const className = `grid gap-1 ${field.wide ? "md:col-span-2" : ""}`;
  if (field.type === "textarea") {
    return (
      <label className={className} htmlFor={id}>
        <span className="field-label">{field.label}</span>
        <textarea id={id} value={String(fieldValue)} onChange={(event) => onChange(event.target.value)} />
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <label className={className} htmlFor={id}>
        <span className="field-label">{field.label}</span>
        <select id={id} value={String(fieldValue)} onChange={(event) => onChange(event.target.value)}>
          <option value="">Not recorded</option>
          {field.options?.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      </label>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-3 rounded-md border border-[#d8d2c4] bg-[#fffdf8] p-3">
        <input type="checkbox" checked={Boolean(fieldValue)} onChange={(event) => onChange(event.target.checked)} />
        <span className="field-label">{field.label}</span>
      </label>
    );
  }
  return (
    <label className={className} htmlFor={id}>
      <span className="field-label">{field.label}</span>
      <input
        id={id}
        type={field.type || "text"}
        value={inputValue}
        onChange={(event) => onChange(field.type === "datetime-local" ? fromDatetimeLocalValue(event.target.value) : event.target.value)}
      />
    </label>
  );
}

function MatchTable({ review }: { review: BuyerFundingReview }) {
  return (
    <div className="mt-5 overflow-auto">
      <table className="data-table">
        <thead><tr><th>Row</th><th>Result</th><th>Expected</th><th>Observed</th><th>Source rows</th></tr></thead>
        <tbody>
          {review.matches.map((match) => (
            <tr key={match.id}>
              <td>{match.label}</td>
              <td>{match.result}</td>
              <td>{match.expectedValue || "Not recorded"}</td>
              <td>{match.observedValue || "Not recorded"}</td>
              <td>{match.sourceRowIds.join(", ") || "Not recorded"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CheckAnswers({ review }: { review: BuyerFundingReview }) {
  return (
    <div className="mt-5 grid gap-4 lg:grid-cols-2">
      <SummaryCard title="File details" icon={<FileText size={17} />}>
        <p>{review.fileNumber || "No file number"} · {review.buyerParties[0]?.name || "No buyer"} · {formatMoney(review.expectedFunds.amount || review.expectedAmount)}</p>
      </SummaryCard>
      <SummaryCard title="Office action" icon={<Lock size={17} />}>
        <p>{review.officeAction.action} · {review.fundsStatus}</p>
      </SummaryCard>
      <SummaryCard title="What stayed open" icon={<AlertTriangle size={17} />}>
        <ul>{review.openGaps.map((gap) => <li key={gap.id}>{gap.gapType}: {gap.description}</li>)}</ul>
      </SummaryCard>
      <SummaryCard title="Save-back note" icon={<Package size={17} />}>
        <p>{review.saveBackLocation || "Not recorded"}</p>
      </SummaryCard>
      {flowEvidenceFor(review) ? (
        <SummaryCard title="Known-entry evidence" icon={<ShieldCheck size={17} />}>
          <p>{flowEvidenceFor(review)?.knownEntryPoint}</p>
          <p>Buyer viewed: {flowEvidenceFor(review)?.buyerViewedAt || "Not recorded"}</p>
          <p>Buyer statement: {flowEvidenceFor(review)?.acknowledgmentStatement || "Not recorded"}</p>
        </SummaryCard>
      ) : null}
    </div>
  );
}

function ExportPanel({
  review,
  receipt,
  onExport,
  onExportZip,
  onCopyBuyerLink,
  onOpenBuyerPortal
}: {
  review: BuyerFundingReview;
  receipt: ReceiptPackage | null;
  onExport: () => void;
  onExportZip: () => void;
  onCopyBuyerLink: () => void;
  onOpenBuyerPortal: () => void;
}) {
  const feasible = buildFeasibleSpecPackage(review);
  const blockedGates = feasible.policyGates.filter((gate) => gate.status === "blocked").length;
  const exceptionGates = feasible.policyGates.filter((gate) => gate.status === "needs_exception").length;

  return (
    <div className="mt-5 space-y-4">
      <div className="flex flex-wrap gap-2">
        <button className="button-primary" onClick={onExport}>
          <Download size={16} /> Export receipt HTML + JSON
        </button>
        <button className="button-secondary" onClick={onExportZip}>
          <Package size={16} /> Download save-back ZIP
        </button>
        <button className="button-secondary" onClick={onCopyBuyerLink}>
          Copy review evidence link
        </button>
        <button className="button-secondary" onClick={onOpenBuyerPortal}>
          Open review evidence intake
        </button>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="panel bg-[#fffdf8]">
          <p className="label">Feasible spec coverage</p>
          <p className="mt-2 text-sm">
            Policy gates: {feasible.policyGates.length} · Blocked: {blockedGates} · Needs exception: {exceptionGates}
          </p>
          <p className="mt-2 text-sm">
            Buyer portal submissions: {review.buyerPortalSubmissions?.length || 0} · Open gaps: {review.openGaps.length}
          </p>
        </div>
        <div className="panel bg-[#fffdf8]">
          <p className="label">Integration posture</p>
          <ul className="mt-2 list-disc pl-5 text-sm">
            {feasible.integrationStatus.map((item) => (
              <li key={item.name}>
                {item.name}: {item.mode}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="panel bg-[#fffdf8]">
        <p className="label">Export package</p>
        <ul className="mt-2 list-disc pl-5 text-sm">
          <li>Buyer Funding Review Receipt HTML (print-ready PDF)</li>
          <li>Save-back ZIP with policy gates, accounting reconciliation, buyer portal packet</li>
          <li>Evidence appendix, source manifest, field cross-reference, event log</li>
          <li>Packet JSON, hashes, redacted summary, save-back note</li>
        </ul>
      </div>
      {receipt ? (
        <div className="panel bg-[#e4efe9]">
          <p className="label">Last export</p>
          <p className="mt-2 text-sm">
            <strong>{receipt.receiptId}</strong> · {receipt.receiptHash.slice(0, 18)}...
          </p>
          <p className="mt-2 text-sm">{receipt.saveBackNote}</p>
          <p className="mt-2 text-sm">Status: {receipt.statusLine}</p>
        </div>
      ) : (
        <p className="text-sm text-[#596158]">No export generated in this browser session for {review.reviewNumber}.</p>
      )}
    </div>
  );
}

function SummaryCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="panel">
      <div className="mb-2 flex items-center gap-2">
        {icon}
        <p className="label">{title}</p>
      </div>
      {children}
    </div>
  );
}

function preserveMoneyCurrency(review: BuyerFundingReview, path: string) {
  if (!path.endsWith(".amountCents")) return;
  const parentPath = path.split(".").slice(0, -1).join(".");
  const parent = getPath(review, parentPath) as { currency?: string } | undefined;
  if (parent && !parent.currency) parent.currency = "USD";
}

function toDatetimeLocalValue(value: unknown): string {
  const text = String(value || "");
  if (!text) return "";
  return text.replace(/Z$/, "").slice(0, 16);
}

function fromDatetimeLocalValue(value: string): string {
  if (!value) return "";
  return value.length === 16 ? `${value}:00.000Z` : value;
}
